/**
 * The page's height-to-width ratio AS DISPLAYED, i.e. the shape of the canvas
 * react-pdf actually draws.
 *
 * react-pdf renders a page at its own `/Rotate` (`Page.js` sizes it through
 * `getViewport({ rotation: page.rotate })`), but the `width`/`height` its
 * `onLoadSuccess` hands back are read straight off `page.view`, the UNROTATED
 * page box (`makePageCallback`). On a quarter-turned page the two disagree: a
 * scanner's landscape box with `/Rotate 90` draws an upright portrait canvas,
 * while `height / width` describes a landscape one. The page wrapper sized from
 * that clipped the canvas to its top half and stretched every page-fraction
 * box over the wrong height.
 *
 * Those two values are also `view[2]`/`view[3]` (the box's x1/y1), not its side
 * lengths, so a crop box that does not start at the origin
 * (`/CropBox [0 200 842 795]`) reads 842x795 for an 842x595 canvas. When the
 * proxy's `view` (`[x0, y0, x1, y1]`) is offset, the sides come from it; a box
 * at the origin keeps the reported `width`/`height` untouched.
 *
 * `rotate` is the pdf.js page proxy's own angle; absent reads as unrotated, so
 * an unrotated page at the origin keeps exactly the `height / width` it always
 * had.
 */
export function pageAspectRatio({
  width,
  height,
  rotate = 0,
  view,
}: {
  width: number
  height: number
  rotate?: number
  view?: readonly number[]
}): number {
  const offset = view !== undefined && (view[0] !== 0 || view[1] !== 0)
  const sideX = offset ? view[2] - view[0] : width
  const sideY = offset ? view[3] - view[1] : height
  const quarterTurn = Math.abs(rotate % 180) === 90
  return quarterTurn ? sideX / sideY : sideY / sideX
}
